<script setup>
import PageTitle from '@weathernews/btos-vuecomponent/with-onebox-common-elements/src/components/PageTitle.vue'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { VueSpinnerPuff } from 'vue3-spinners'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  mapPosition: { type: Object, required: true },
  title: { type: String, default: '' }
})

const config = useRuntimeConfig()
const windowWidth = ref(0)
const mapRef = ref(null)
const containerHeight = ref('')
const mapContainer = ref(null)
const mapZoom = ref(3.2)
const mapCenter = ref(props.mapPosition.center)
const windList = ref([])
const pointName = ref('')
const latlon = ref('')
const now = ref(dayjs().startOf('hour'))
const formatTitle = props.title ? `${props.title}の風の予報（風レーダー）` : '風の予報（風レーダー）'
const isLoading = ref(false)
const isMapLoaded = ref(false)
const isShowTable = ref(false)
const isModal = ref(false)
const windModal = useCookie('windModal', { default: () => null, maxAge: 60 * 60 * 24 * 30 })
if (import.meta.client) {
  window.addEventListener('custom-geolocate-loading', (e) => {
    if (e && e.detail && typeof e.detail.loading === 'boolean') {
      isLoading.value = e.detail.loading
    }
  })
  windowWidth.value = window.innerWidth
  window.addEventListener('resize', () => {
    windowWidth.value = window.innerWidth
    setContainerHeight()
  })
}

const computedStyle = computed(() => {
  const height = (windowWidth.value - 20) / 75 * 68 - (168 - 91) // (スマホの画面横幅 - 横padding) / 横の比率 * 高さの比率 - (WindTableの高さ - WindControlの高さ)
  return windowWidth.value >= 830
    ? { height: containerHeight.value }
    : { height: `${height}px` }
})
const [{ data: wndspdData }, { data: windData }] = await Promise.all([
  useFetch(`${config.public.wndspdTileUrl}wndspd`),
  useFetch(`${config.public.wndspdTileUrl}wind`)
])
const timeList = Object.keys(wndspdData.value.basetimes)
  .toSorted((a, b) => dayjs(a).unix() - dayjs(b).unix())
  .filter((t) => {
    const d = dayjs(t)
    return d.isSameOrAfter(now.value) && d.isSameOrBefore(now.value.add(72, 'hour'))
  })
const sliderValue = ref(0)
const selectedTime = computed(() => timeList[sliderValue.value])
const tileUrlList = timeList.map((time) => {
  return `${config.public.wndspdTileDomain}${wndspdData.value.urls.tile}`.replace('{basetime}/{ft}', wndspdData.value.basetimes[time])
})
const binUrl = computed(() => `${config.public.wndspdTileDomain}${windData.value.urls.clip}`.replace('{basetime}/{ft}', windData.value.basetimes[selectedTime.value]))
const { initMap, destroyMap, setWndspdLayer, updateWndspdLayer, setWindStreamLayer, updateWindStreamLayer, clickPoint, tmpMarkerPosition, isInheritPin, loadedMap } = useWindMap(binUrl, sliderValue, tileUrlList, wndspdData.value.options)

const isShowPin = ref(clickPoint.value !== null)
const setContainerHeight = () => {
  if (mapRef.value && isShowTable.value) {
    const parentWidth = mapRef.value.offsetWidth
    containerHeight.value = `${parentWidth / 151 * 100 - (183 - 91)}px` // map-containerの横幅 / 横の比率 * 高さの比率 - (WindTableの高さ - WindControlの高さ)
  } else {
    containerHeight.value = ''
  }
}

watch(sliderValue, async (newValue, oldValue) => {
  updateWndspdLayer(oldValue)
  updateWindStreamLayer()
})

watch(clickPoint, async (newPoint) => {
  if (newPoint) {
    isShowPin.value = true
    const latlonQuery = `${newPoint.lat}/${newPoint.lon}`
    const pointData = await $fetch(`${config.public.wndspdTileDomain}${windData.value.urls.point}`.replace('{lat}/{lon}', latlonQuery))
    if (pointData.auth === 'NG') {
      isShowTable.value = false
      isShowPin.value = false
      isInheritPin.value = false
      return
    }
    const format = formattedWindList(pointData, wndspdData.value.color.palette)
    windList.value = format
    const pointInfo = await $fetch(`${config.public.siteDataDomain}/lba/revgeo`, { query: { latlon: latlonQuery } })
    const pref = pointInfo[latlonQuery].city.pref || ''
    const city = pointInfo[latlonQuery].city.name || ''
    pointName.value = `${pref}${city}`
    latlon.value = `（${newPoint.lat.toFixed(3)}/${newPoint.lon.toFixed(3)}）`
    if (isInheritPin.value) {
      isInheritPin.value = false
    } else {
      isShowTable.value = true
    }
  }
})

watch(isShowTable, setContainerHeight)

watch(isModal, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      isModal.value = false
    }, 1000)
  }
})

onMounted(async () => {
  await initMap(mapContainer.value, mapZoom, mapCenter, props.mapPosition.bounds)
  isMapLoaded.value = true
  setWndspdLayer()
  setWindStreamLayer()
  setContainerHeight()
  mapCenter.value = [loadedMap.value.getCenter().lng, loadedMap.value.getCenter().lat]
  mapZoom.value = loadedMap.value.getZoom()
  if (!windModal.value) {
    isModal.value = true
    windModal.value = 1
  }
})
onUnmounted(() => {
  destroyMap()
})
</script>

<template>
  <section class="zoomMap">
    <div class="head">
      <PageTitle :title="formatTitle" />
    </div>
    <div
      ref="mapRef"
      class="map"
    >
      <img
        src="https://gvs.weathernews.jp/onebox/radar/img/legend/legend_zoomradar_wind_grad@2x.png"
        alt="legend"
      >
      <div
        ref="mapContainer"
        class="map-container"
        :style="isShowTable ? computedStyle : {}"
      >
        <div
          class="first__modal"
          :data-visible="isModal"
        >
          <p>地図をタップすると<br>風向・風速が表示されます。</p>
        </div>
        <div
          v-if="isLoading"
          class="loading-indicator"
        >
          <VueSpinnerPuff
            size="70"
            color="#ffffff"
          />
        </div>
        <div
          v-if="isMapLoaded"
          class="map__buttons"
        >
          <WindRadarLinkButton
            :map-center="mapCenter"
            :map-zoom="mapZoom"
            :tmp-marker-position="tmpMarkerPosition"
          />
        </div>
      </div>
      <WindControl
        v-if="!isShowTable"
        v-model:slider-value="sliderValue"
        v-model:is-show-table="isShowTable"
        v-model:is-modal="isModal"
        :time-list="timeList"
        :is-show-pin="isShowPin"
      />
      <WindTable
        v-if="isShowTable"
        v-model:is-show-table="isShowTable"
        :slider-value="sliderValue"
        :wind-list="windList"
        :latlon="latlon"
        :point-name="pointName"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.zoomMap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .map {
    position: relative;
    img {
      position: absolute;
      top: 5px;
      left: 5px;
      width: 150px;
      height: auto;
      z-index: 10;
      pointer-events: none;
    }
    .map-container {
      position: relative;
      width: 100%;
      aspect-ratio: 75 / 68;
      .loading-indicator {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.75);
        z-index: 100;
        pointer-events: none;
      }
      .map__buttons {
        position: absolute;
        bottom: 0;
        right: 0;
        padding: 7px;
        z-index: 3;
        pointer-events: auto;
      }
      .first__modal {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgb(0 0 0 / .75);
        z-index: 10;
        transition: opacity .2s ease-in-out;
        p {
          color: #FFF;
          text-align: center;
          font-size: 20px;
          font-weight: 500;
          line-height: 1.5em;
          user-select: none;
        }
        &[data-visible="false"] {
          opacity: 0;
          pointer-events: none;
        }
      }
    }
  }
}
:deep(.maplibregl-ctrl-group) {
  position: relative;
  box-shadow: 0 2px 4px 0 rgb(0 0 0 / .25);
}
:deep(.maplibregl-ctrl-group:has(.circle)) {
  border-radius: 50%;
  border: 2px solid #FFF;
  background-color: #E6EAEC;
}
:deep(.maplibregl-ctrl-group button) {
  width: 30px;
  height: 30px;
}
:deep(.maplibregl-ctrl-group button.circle) {
  width: 35px;
  height: 35px;
}
:deep(.maplibregl-ctrl-group button:is(.map__balloon *)) {
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 4px 0 rgb(0 0 0 / .25);
}
:deep(.maplibregl-ctrl-bottom-left) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  left: 0;
  bottom: 0;
  padding: 7px;
  pointer-events: auto;
}
:deep(.maplibregl-ctrl-bottom-left > *) {
  margin: 0;
}
:deep(.maplibregl-ctrl-bottom-left .maplibregl-compact) {
  margin-inline: 3px;
}
:deep(.maplibregl-ctrl-bottom-right) {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  right: 7px;
  bottom: 7px;
}
:deep(.maplibregl-ctrl-bottom-right > *) {
  margin: 0;
}
@media screen and (min-width: 830px) {
  .zoomMap {
    .map {
      &__radars {
        &-inner {
          width: auto;
          &:is(.active *) {
            width: auto;
          }
        }
        &::before {
          display: none;
        }
      }
      img {
        width: 180px;
      }
      .map-container {
        aspect-ratio: 151 / 100;
        .map__buttons {
          padding: 9px;
        }
        .first__modal {
          p {
            font-size: 30px;
          }
        }
      }
    }
  }
  :deep(.maplibregl-ctrl-group:has(.circle)) {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 150px;
    padding: 5px 8px 5px 5px;
    border-radius: 6px;
    border: none;
    background-color: #FFF;
    cursor: pointer;
    transition: background-color .2s ease-in-out;
  }
  :deep(.maplibregl-ctrl-group:has(.circle):hover) {
    background-color: #F2F2F2;
  }
  :deep(.maplibregl-ctrl-group button.circle) {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: #E6EAEC;
  }
  :deep(.maplibregl-ctrl-bottom-left) {
    gap: 6px;
    padding: 9px;
  }
}
</style>
